import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MomentModule } from 'ngx-moment';
import { MatDialog } from '@angular/material/dialog';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { InputTextModule } from '../../../../components/input/input-text';
import { InputTextareaModule } from '../../../../components/input/input-textarea';
import { InputHelperService } from '../../../../components/input/input-helper';
import { AppConfig } from '../../../app.config';
import { TicketHistoryComponent } from '../../../../components/ticket-history';
import { DirectivesModule } from '../../../../components/directives.module';
import { InputSelectModule } from '../../../../components/input/input-select';
import { InputType } from '../../../../../typings/client/input';
import { Broadcaster } from '../../../../components/broadcaster';
import { TicketMessageModule } from '../ticket-message/ticket-message.module';
import { OperatorAnalysisHistoryComponent } from 'src/components/operator-analysis-history';
import { SbarHistoryComponent } from '../sbar-history';
import { ConnectionService } from '../../../../services/connection-service';
import {
  getDiscomfortDynamicFields,
  getTreatmentDynamicFields,
  getOrdersDynamicFields,
  OTHER_DYNAMIC_FIELDS,
  getSubcategoriesForCategory,
  getInstructionDynamicFields
} from './ticket-dynamic-fields.config';

interface DynamicField<T = string> {
  name: T;
  label?: string;
  type: 'select' | 'text' | 'textArea' | 'radio';
  options?: Array<InputType.SelectOption>;
  placeholder?: string;
  required?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-ticket-details',
  templateUrl: './ticket-details.html',
  styleUrls: ['./ticket-details.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DirectivesModule,
    MomentModule,
    InputSelectModule,
    InputTextModule,
    InputTextareaModule,
    TicketMessageModule,
  ],
})
export class TicketDetailsComponent implements OnInit {
  @Input() ticket: Table.SupportTicket;
  @Input() user: Table.User;
  operatorAnalysisResponse: Table.SupportTicket['attributes']['operatorAnalysisResponse'] = { category: '', subcategory: '' };
  dynamicFields: DynamicField[] = [];
  editMode: boolean = true;
  categories: Array<InputType.SelectOption> = [
    { display: 'discomfort', value: 'discomfort' },
    { display: 'order', value: 'order' },
    { display: 'treatment', value: 'treatment' },
    { display: 'instruction', value: 'instruction' },
    { display: 'other', value: 'default' },
    { display: 'faq', value: 'faq' },
    { display: 'hair', value: 'hair' },
  ];
  subcategories: Array<InputType.SelectOption> = [];
  allocatedTo: string;
  currentUser: Table.User;

  constructor(
    private inputHelper: InputHelperService,
    private broadcaster: Broadcaster,
    private appConfig: AppConfig,
    private dialog: MatDialog,
    private conn: ConnectionService
  ) {}

  ngOnInit(): void {
    if (!this.ticket?.get('operatorAnalysisResponse') && this.ticket?.get('llmResponse')) {
      this.operatorAnalysisResponse.category = this.ticket.get('llmResponse')?.category;
      this.onCategoryChanged(this.ticket.get('llmResponse')?.category);
      this.onSubcategoryChanged(this.ticket.get('llmResponse')?.subcategory);
      this.operatorAnalysisResponse.subcategory = this.ticket.get('llmResponse')?.subcategory;
    } else if (this.ticket?.get('operatorAnalysisResponse')?.category) {
      this.operatorAnalysisResponse.category = this.ticket?.get('operatorAnalysisResponse')?.category;
      this.onCategoryChanged(this.ticket?.get('operatorAnalysisResponse')?.category as string);
      this.onSubcategoryChanged(this.ticket?.get('operatorAnalysisResponse')?.subcategory as string);
    }
    this.initializeOperatorAnalysisResponse();
    this.determineInitialEditMode();
    this.getAllocatedOperator();
    this.currentUser = this.conn.getCurrentUser();
  }

  private initializeOperatorAnalysisResponse(): void {
    this.dynamicFields.forEach(field => {
      this.operatorAnalysisResponse[field.name] = this.ticket?.get('operatorAnalysisResponse')[field.name];
    });
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
  }

  determineInitialEditMode(): void {
    const isBaseFieldsMissing = this.checkBaseFieldsMissing();
    this.editMode = isBaseFieldsMissing;
    this.updateDynamicFields();
    if (!this.editMode) {
      this.editMode = this.checkDynamicFieldsMissing();
    }
    if(this.operatorAnalysisResponse?.callStatus === 'RNR') {
      this.editMode = false;
    }
  }

  updateDynamicFields(): void {
    const category = this.operatorAnalysisResponse.category || this.ticket.get('operatorAnalysisResponse')?.category;
    switch (category) {
      case ApiClientConstant.TicketCategory.Discomfort:
        this.dynamicFields = this.getDiscomfortIssueDynamicFields();
        break;
      case ApiClientConstant.TicketCategory.Treatment:
        this.dynamicFields = this.getTreatmentIssueDynamicFields();
        break;
      case ApiClientConstant.TicketCategory.Order:
        this.dynamicFields = this.getOrdersIssueDynamicFields();
        break;
      case ApiClientConstant.TicketCategory.Instruction:
        this.dynamicFields = this.getInstructionIssueDynamicFields();
        break;
      case ApiClientConstant.TicketCategory.Default:
      case ApiClientConstant.TicketCategory.Faq:
      case 'hair' as any:
        this.dynamicFields = this.getOtherCategoryDynamicFields();
        break;
      default:
        this.dynamicFields = [];
    }
    if (this.operatorAnalysisResponse?.callStatus === 'RNR') {
      this.hideDynamicFields();
    }
  }

  private getDiscomfortIssueDynamicFields(): DynamicField[] {
    return getDiscomfortDynamicFields(this.subcategories, this.operatorAnalysisResponse.subcategory);
  }

  private getTreatmentIssueDynamicFields(): DynamicField[] {
    return getTreatmentDynamicFields(this.subcategories, this.operatorAnalysisResponse.subcategory);
  }

  private getOrdersIssueDynamicFields(): DynamicField[] {
    return getOrdersDynamicFields(this.subcategories, this.operatorAnalysisResponse.subcategory);
  }

  private getInstructionIssueDynamicFields(): DynamicField[] {
    return getInstructionDynamicFields(this.subcategories, this.operatorAnalysisResponse.subcategory);
  }

  private getOtherCategoryDynamicFields(): DynamicField[] {
    return OTHER_DYNAMIC_FIELDS;
  }

  private checkDynamicFieldsMissing(): boolean {
    const requiredDynamicFields = this.dynamicFields.filter((field: DynamicField) => field.required);
    return requiredDynamicFields.some((field: DynamicField) => {
      const value = this.operatorAnalysisResponse[field.name];
      return value === null || value === undefined || value === '';
    });
  }
  private checkBaseFieldsMissing(): boolean {
    const ticketAnalysis = this.ticket?.get('operatorAnalysisResponse');
    const category = ticketAnalysis?.category || this.operatorAnalysisResponse.category;
    const subcategory = ticketAnalysis?.subcategory || this.operatorAnalysisResponse.subcategory;
    if (category === 'default') {
      return !category;
    }
    return !category || !subcategory;
  }

  onCategoryChanged(event: string): void {
    this.subcategories = getSubcategoriesForCategory(event);
    this.operatorAnalysisResponse.subcategory = '';
    this.resetDynamicFields();
    this.dynamicFields = [];
    if(this.operatorAnalysisResponse.category === 'default') {
      this.updateDynamicFields();
    }
  }

  onSubcategoryChanged(event: string): void {
    if(this.operatorAnalysisResponse.category === 'default') {
      return;
    }
    this.operatorAnalysisResponse.subcategory = event;
    this.resetDynamicFields();
    this.updateDynamicFields();
    this.checkTicketCompleteness()
  }

  private resetDynamicFields(): void {
    this.dynamicFields.forEach((field: DynamicField) => {
      this.operatorAnalysisResponse[field.name] = null;
    });
  }

  private checkTicketCompleteness(): void {
    const isBaseFieldsMissing = this.checkBaseFieldsMissing();
    if (!isBaseFieldsMissing) {
      this.updateDynamicFields();
      this.editMode = this.checkDynamicFieldsMissing();
    } else {
      this.editMode = true;
    }
  }

  areDynamicFieldsPopulated(): boolean {
    const requiredFields = this.dynamicFields.filter((field: DynamicField) => field.required);
    return requiredFields.every((field: DynamicField) => {
      const value = this.operatorAnalysisResponse[field.name];
      return value !== null && value !== undefined && value !== '';
    });
  }

  addProductsUsedField(event: boolean): void {
    if (event === true) {
      this.dynamicFields.push({
        name: 'otherProductsName',
        label: 'Name of Other Products used',
        type: 'textArea',
        required: false,
        placeholder: 'write the names of other products used'
      })
    } else {
      this.dynamicFields = this.dynamicFields.filter((field: DynamicField) => field.name !== 'otherProductsName');
      this.operatorAnalysisResponse.otherProductsName = null;
    }
  }

  async saveTicketDetails(): Promise<void> {
    if (this.areDynamicFieldsPopulated()) {
      try {
        this.ticket.set('operatorAnalysisResponse', this.operatorAnalysisResponse)
        await this.ticket.save();
        this.editMode = false;
      } catch (error) {
        this.broadcaster.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
      }
    } else {
      this.broadcaster.broadcast('NOTIFY', { message: 'Some details are missing', type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }

  async updateTicketAttendedStatus(attended: boolean): Promise<void> {
    try {
      this.ticket.set('attended', attended);
      this.ticket = await this.ticket.save();
    } catch ({ message }) {
      this.broadcaster.broadcast('NOTIFY', { message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }

  openTicketHistory(ticket: Table.SupportTicket): void {
    this.dialog.open(TicketHistoryComponent, { width: '70%', data: { ticket } });
  }

  openOperatorAnalysisHistory(ticket: Table.SupportTicket): void {
    this.dialog.open(OperatorAnalysisHistoryComponent, { width: '70%', data: { ticket } });
  }

  openSbarHistory(): void {
    this.dialog.open(SbarHistoryComponent, {
      width: '95%',
      data: { supportTicket: this.ticket },
    });
  }

  getAllocatedOperator(): void {
    if (!this.ticket?.get('teams')) return;
    if (this.ticket?.get('teams')[0] === ApiClientConstant.Role.Name.DOCTOR) {
      this.allocatedTo = this.ticket?.get('allocatedDoctor')?.get('DoctorDisplayName') || '';
    } else {
      this.allocatedTo = this.ticket?.get('allocatedOperator')?.get('username') || '';
    }
  }

  formatKey(key: string): string {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  }

  getKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  onCallStatusChange(event: string): void {
    if (event === 'RNR') {
      this.hideDynamicFields();
    } else {
      this.updateDynamicFields();
    }
  }

  hideDynamicFields(): void {
    this.dynamicFields.forEach((item: DynamicField) => {
      if (!['category', 'subcategory', 'callStatus'].includes(item.name)) {
        if (item.type === 'select' || item.type === 'textArea') {
          this.operatorAnalysisResponse[item.name] = ''
        } else {
          this.operatorAnalysisResponse[item.name] = undefined;
        }
      }
    })
    this.dynamicFields = this.dynamicFields.filter((item: DynamicField) => ['category', 'subcategory', 'callStatus'].includes(item.name));
  }

  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
